// Core
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Utils
import {
  ciclesTypes,
  contractTypes,
  voltageLevelTypes,
  contractedPowerOptions,
} from "utils/constants/index";

// Components
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import BoxV1 from "components/Atoms/Box/BoxV1";
import DatePicker from "components/Atoms/DatePicker";
import TypographyV1 from "components/Atoms/Typography/TypographyV1";
import FormField from "components/InputFields/FormFieldV1";
import SelectWithSearch from "components/InputFields/SelectWithSearch";
import IndexedSimplified from "pages/ManageContracts/Single/ContractCard/IndexedSimplified";
import Koefficients from "pages/ManageContracts/Single/ContractCard/Koefficients";
import Tariffs from "pages/ManageContracts/Single/ContractCard/Tariffs";

function renderPayloadDetails({ type, payload, onChange }) {
  if (type === "INDEXED") {
    return <Koefficients data={payload} action="new" onChange={onChange} />;
  }
  if (type === "FIXED") {
    return <Tariffs data={payload} action="new" onChange={onChange} />;
  }
  if (type === "INDEXED_SIMPLIFIED") {
    return <IndexedSimplified data={payload} onChange={onChange} action="new" />;
  }
  return null;
}

// Bridges MUI Select event → react-select-style { value } object expected by Form.js handlers
const asOption = (e) => ({ value: e.target.value });

const switchSx = (theme) =>
  theme.palette.mode === "dark"
    ? {
        "& .MuiSwitch-track": { opacity: 1 },
        "& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb": {
          color: "rgba(255,255,255,0.7)",
        },
        "& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track": {
          backgroundColor: "#6b7280",
          opacity: 1,
        },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
          backgroundColor: "#4caf50",
          opacity: 1,
        },
      }
    : {};

function ContractCard({
  contract,
  buyType,
  sellType,
  members,
  onChangeMember,
  onChangeValidity,
  onChangeVoltageLevel,
  voltageLevel,
  onChangeTariffCycle,
  onChangeContractedPower,
  contractedPower,
  onChangeWithHolidays,
  onChangeBuyType,
  onChangeSellType,
  buyPayload,
  sellPayload,
  onChangeBuyPayload,
  onChangeSellPayload,
}) {
  const { t } = useTranslation(["contracts", "common"]);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const tCycles = ciclesTypes.map((o) => ({
    value: o.value,
    label: t(`options.cicles.${o.value}`),
  }));
  const tContractTypes = contractTypes.map((o) => ({
    value: o.value,
    label: t(`options.types.${o.value}`),
  }));
  const tVoltageLevels = voltageLevelTypes
    .filter((o) => o.value !== "BT")
    .map((o) => ({
      value: o.value,
      label: t(`options.voltage_levels.${o.value}`, { defaultValue: o.label }),
    }));

  const validity = contract?.validity || new Date().toISOString();
  const withHoliday = contract?.with_holidays;

  const BTN_SIMPLE = ["BTN simples", "BTN bi-horária", "BTN< tri-horária"];
  const isBTNSimple = BTN_SIMPLE.includes(voltageLevel);
  const isBTNPlus = voltageLevel === "BTN>";
  const showContractedPowerSelect = isBTNSimple || isBTNPlus;

  const contractedPowerSelectOptions = contractedPowerOptions
    .filter((opt) =>
      isBTNSimple
        ? [1.15, 2.3, 3.45, 4.6, 5.75, 6.9, 10.35, 13.8, 17.25, 20.7].includes(opt.value)
        : [27.6, 34.5, 41.4].includes(opt.value)
    )
    .map((opt) => ({ value: String(opt.value), label: opt.label }));

  const contractedPowerInput = showContractedPowerSelect ? (
    <SelectWithSearch
      label={t("form.contracted_power")}
      value={String(contractedPower)}
      options={contractedPowerSelectOptions}
      onChange={(e) => onChangeContractedPower({ value: parseFloat(e.target.value) }, "select")}
      minWidth="100%"
    />
  ) : (
    <FormField
      type="number"
      label={t("form.contracted_power")}
      defaultValue={contractedPower}
      onChange={(e) => onChangeContractedPower(e, "formfield")}
    />
  );

  const buyDetails = renderPayloadDetails({
    type: buyType,
    payload: buyPayload,
    onChange: onChangeBuyPayload,
  });
  const sellDetails = renderPayloadDetails({
    type: sellType,
    payload: sellPayload,
    onChange: onChangeSellPayload,
  });

  return (
    // overflow:hidden prevents MUI Grid negative margins from causing horizontal scroll
    <Box sx={{ overflow: "hidden", py: 1 }}>
      {/* Row 1: Member, Validity, Voltage Level, Tariff Cycle */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SelectWithSearch
            label={t("form.member_responsible")}
            value={contract?.member ?? ""}
            onChange={(e) => onChangeMember(asOption(e))}
            options={members.map((m) => ({ label: m.name, value: m.pre_id }))}
            minWidth="100%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <BoxV1 mb={1} ml={0.5} lineHeight={0} display="inline-block">
            <TypographyV1 component="label" variant="caption" fontWeight="bold">
              {t("form.valid_since")}
            </TypographyV1>
          </BoxV1>
          <DatePicker defaultValue={validity} onChange={onChangeValidity} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SelectWithSearch
            label={t("form.voltage_level")}
            value={contract?.voltage_level ?? ""}
            onChange={(e) => onChangeVoltageLevel(asOption(e))}
            options={tVoltageLevels}
            minWidth="100%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SelectWithSearch
            label={t("form.tariff_cycle")}
            value={contract?.tariff_cycle ?? ""}
            onChange={(e) => onChangeTariffCycle(asOption(e))}
            options={tCycles}
            minWidth="100%"
          />
        </Grid>
      </Grid>

      {/* Row 2: Contracted Power, Holidays, Buy Type, Sell Type */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          {contractedPowerInput}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <BoxV1 mb={1} ml={0.5} lineHeight={0} display="inline-block">
            <TypographyV1 component="label" variant="caption" fontWeight="bold">
              {t("form.with_holidays")}
            </TypographyV1>
          </BoxV1>
          <BoxV1 ml={0.5}>
            <Switch defaultChecked={!!withHoliday} onChange={onChangeWithHolidays} sx={switchSx} />
          </BoxV1>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SelectWithSearch
            label={t("form.buy_type")}
            value={buyType}
            onChange={(e) => onChangeBuyType(asOption(e))}
            options={tContractTypes}
            minWidth="100%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SelectWithSearch
            label={t("form.sell_type")}
            value={sellType}
            onChange={(e) => onChangeSellType(asOption(e))}
            options={tContractTypes}
            minWidth="100%"
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2 }} />

      {/* Buy & Sell details side by side */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TypographyV1
            variant="h6"
            fontWeight="bold"
            mb={1.5}
            sx={isDark ? { color: "#ffffff" } : {}}
          >
            {t("form.buy_details_title")}
          </TypographyV1>
          {buyDetails}
        </Grid>
        <Grid item xs={12} md={6}>
          <TypographyV1
            variant="h6"
            fontWeight="bold"
            mb={1.5}
            sx={isDark ? { color: "#ffffff" } : {}}
          >
            {t("form.sell_details_title")}
          </TypographyV1>
          {sellDetails}
        </Grid>
      </Grid>
    </Box>
  );
}

ContractCard.defaultProps = {
  contract: null,
  onChangeMember: null,
  onChangeValidity: null,
  onChangeVoltageLevel: null,
  voltageLevel: null,
  onChangeTariffCycle: null,
  onChangeContractedPower: null,
  contractedPower: 0,
  onChangeWithHolidays: null,
  onChangeBuyType: null,
  onChangeSellType: null,
  buyPayload: null,
  sellPayload: null,
  onChangeBuyPayload: null,
  onChangeSellPayload: null,
};

ContractCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  members: PropTypes.array.isRequired,
  buyType: PropTypes.string.isRequired,
  sellType: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  contract: PropTypes.object,
  onChangeMember: PropTypes.func,
  onChangeValidity: PropTypes.func,
  onChangeVoltageLevel: PropTypes.func,
  voltageLevel: PropTypes.string,
  onChangeTariffCycle: PropTypes.func,
  onChangeContractedPower: PropTypes.func,
  contractedPower: PropTypes.number,
  onChangeWithHolidays: PropTypes.func,
  onChangeBuyType: PropTypes.func,
  onChangeSellType: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  buyPayload: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  sellPayload: PropTypes.object,
  onChangeBuyPayload: PropTypes.func,
  onChangeSellPayload: PropTypes.func,
};

export default ContractCard;
